import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { StoreLogo } from '../../../shared/store-logo/store-logo';

/** Data the store page hands the offer modal. */
export interface OfferModalData {
  id: string;
  title: string;
  code: string; // '' for deal-only offers
  isDeal: boolean;
  expiryText: string;
  detail: string; // exclusions / fine print (plain text)
  storeName: string;
  storeLogoUrl: string;
  storeSlug: string;
  /** Cloaked affiliate redirect (`/go/:store/:offerId`). */
  goUrl: string;
  /** Where "View all products" navigates. */
  productsPath: string;
}

/**
 * Coupert-style offer modal — opens on Get Code / Get Deal, over a blurred page. For codes it shows
 * the code + copy; for deals it shows a single "Activate" CTA (no code). Purely presentational: the
 * store page owns opening/closing (and the URL `?coupon=` state).
 */
@Component({
  selector: 'app-offer-modal',
  standalone: true,
  imports: [StoreLogo],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './offer-modal.html',
  styleUrl: './offer-modal.scss',
})
export class OfferModal {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  readonly offer = input.required<OfferModalData>();
  readonly closed = output<void>();
  /**
   * Raised when the shopper copies the code. The modal stays presentational — the store page owns
   * the API and decides what a copy is worth, the same way it owns open/close.
   */
  readonly codeCopied = output<string>();

  protected readonly copied = signal(false);
  private readonly copyBtn = viewChild<ElementRef<HTMLButtonElement>>('copyBtn');

  constructor() {
    if (this.isBrowser) {
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') this.close();
      };
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
      this.destroyRef.onDestroy(() => {
        document.removeEventListener('keydown', onKey);
        document.body.style.overflow = '';
      });
    }
    // Move focus to the primary action for accessibility once rendered.
    effect(() => {
      const btn = this.copyBtn();
      if (btn) btn.nativeElement.focus();
    });
  }

  protected close(): void {
    this.closed.emit();
  }

  /** Re-open the affiliate destination (a fresh tab) from inside the modal. */
  protected goToStore(): void {
    if (this.isBrowser) window.open(this.offer().goUrl, '_blank', 'noopener');
  }

  protected copyCode(): void {
    const code = this.offer().code;
    if (!code || !this.isBrowser) return;
    if (navigator.clipboard) navigator.clipboard.writeText(code).catch(() => undefined);
    this.copied.set(true);
    this.codeCopied.emit(this.offer().id);
    setTimeout(() => this.copied.set(false), 1600);
  }
}
